import { appInject } from "@core/di/utils";
import { DI_TOKENS } from "@shared/constants/di";
import { IAchievementsService } from "@shared/interfaces/achievements-service.interface";
import { HttpErrorResponse } from "@shared/models/error/http-error-response";
import { AchievementDto } from "@shared/types/achievements/achievements.dto";
import { UserDto } from "@shared/types/user/user.dto";
import { Alert, Button, Modal, notification, Select, Space } from "antd";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery } from "react-query";

const ALREADY_ASSIGNED_MESSAGE =
  "This achievement is already assigned to this user.";

function getAssignErrorMessage(error: unknown): string {
  if (error instanceof HttpErrorResponse) {
    const apiMessage = error.errors.get("message" as never);
    if (typeof apiMessage === "string" && apiMessage) {
      return apiMessage;
    }
    if (Array.isArray(apiMessage) && apiMessage[0]) {
      return apiMessage[0];
    }

    const first = error.findFirstErrorMessage();
    if (first) {
      return first;
    }

    if (error.status === 409) {
      return ALREADY_ASSIGNED_MESSAGE;
    }
  }
  return "Failed to assign achievement. Please try again.";
}

interface AssignAchievementModalProps {
  open: boolean;
  user: UserDto | null;
  onClose: () => void;
  onSuccess: () => void;
}

const AssignAchievementModal = ({
  open,
  onClose,
  onSuccess,
  user,
}: AssignAchievementModalProps) => {
  const [step, setStep] = useState<"select" | "confirm">("select");
  const [selectedAchievementId, setSelectedAchievementId] = useState<
    string | null
  >(null);
  const [searchQuery, setSearchQuery] = useState("");

  const achievementsService = appInject<IAchievementsService>(
    DI_TOKENS.achievementsService,
  );

  const achievementsQuery = useQuery(
    ["achievements-list", searchQuery],
    () =>
      achievementsService.getAchievementsList({
        page: 1,
        pageSize: 100,
        query: searchQuery || undefined,
      }),
    { enabled: open },
  );

  const successNotifiedRef = useRef(false);

  const assignMutation = useMutation(
    (body: Parameters<IAchievementsService["assignAchievement"]>[0]) =>
      achievementsService.assignAchievement(body),
  );

  const resetForm = useCallback(() => {
    setStep("select");
    setSelectedAchievementId(null);
    setSearchQuery("");
  }, []);

  const handleClose = useCallback(() => {
    resetForm();
    assignMutation.reset();
    onClose();
  }, [onClose, resetForm, assignMutation]);

  useEffect(() => {
    if (open) {
      successNotifiedRef.current = false;
    }
  }, [open]);

  const achievements = useMemo(
    () => achievementsQuery.data?.achievements ?? [],
    [achievementsQuery.data?.achievements],
  );

  const selectedAchievement = selectedAchievementId
    ? achievements.find((a) => a.id === selectedAchievementId)
    : null;

  const achievementOptions = useMemo(() => {
    const byCategory = new Map<string, AchievementDto[]>();

    for (const achievement of achievements) {
      const categoryName = achievement.category?.name ?? "Uncategorized";
      const group = byCategory.get(categoryName) ?? [];
      group.push(achievement);
      byCategory.set(categoryName, group);
    }

    return Array.from(byCategory.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([categoryName, items]) => ({
        label: categoryName,
        options: items.map((achievement) => ({
          value: achievement.id,
          label: achievement.subCategory?.name
            ? `${achievement.subCategory.name} - ${achievement.name}`
            : achievement.name,
        })),
      }));
  }, [achievements]);

  const formatAchievementLabel = (achievement: AchievementDto) => {
    const categoryName = achievement.category?.name;
    const achievementLabel = achievement.subCategory?.name
      ? `${achievement.subCategory.name} - ${achievement.name}`
      : achievement.name;

    return categoryName
      ? `${categoryName} / ${achievementLabel}`
      : achievementLabel;
  };

  const displayName =
    user?.nickname ?? user?.email ?? user?.id ?? "this user";

  const handleConfirmAssign = () => {
    if (!user?.id || !selectedAchievementId) {
      return;
    }

    assignMutation.mutate(
      {
        userId: user.id,
        achievementId: selectedAchievementId,
        meta: {
          type: "manual",
          assignedBy: "admin",
        },
        hasViewed: false,
      },
      {
        onSuccess: (res) => {
          if (successNotifiedRef.current) {
            return;
          }
          successNotifiedRef.current = true;

          console.log("res", res);

          notification.success({
            message:
              res?.message ?? "Achievement assigned successfully.",
          });
          onSuccess();
          resetForm();
          assignMutation.reset();
          onClose();
        },
      },
    );
  };

  return (
    <Modal
      title="Assign Achievement"
      open={open}
      onCancel={handleClose}
      footer={null}
      destroyOnClose
    >
      {step === "select" && (
        <Space direction="vertical" style={{ width: "100%" }} size="middle">
          <div>
            <label style={{ display: "block", marginBottom: 8 }}>
              Achievement
            </label>
            <Select
              placeholder="Search and select an achievement"
              showSearch
              allowClear
              loading={achievementsQuery.isLoading}
              value={selectedAchievementId}
              onChange={setSelectedAchievementId}
              onSearch={setSearchQuery}
              filterOption={false}
              style={{ width: "100%" }}
              options={achievementOptions}
            />
          </div>
          <Space style={{ justifyContent: "flex-end", width: "100%" }}>
            <Button onClick={handleClose}>Cancel</Button>
            <Button
              type="primary"
              disabled={!selectedAchievementId}
              onClick={() => setStep("confirm")}
            >
              Next
            </Button>
          </Space>
        </Space>
      )}

      {step === "confirm" && selectedAchievement && (
        <>
          <Alert
            type="info"
            message={`Assign "${formatAchievementLabel(selectedAchievement)}" to ${displayName}?`}
            description="This will add the achievement to the user's account as if they had earned it in the app."
            style={{ marginBottom: 16 }}
          />
          {assignMutation.isError && (
            <Alert
              type="error"
              message={getAssignErrorMessage(assignMutation.error)}
              style={{ marginBottom: 16 }}
            />
          )}
          <Space style={{ justifyContent: "flex-end", width: "100%" }}>
            <Button
              onClick={() => {
                setStep("select");
                assignMutation.reset();
              }}
            >
              Back
            </Button>
            <Button onClick={handleClose}>Cancel</Button>
            <Button
              type="primary"
              loading={assignMutation.isLoading}
              onClick={handleConfirmAssign}
            >
              Confirm
            </Button>
          </Space>
        </>
      )}
    </Modal>
  );
};

export default AssignAchievementModal;
